import React, { useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Calendar, MapPin, Building2, Wrench, CheckCircle2, Phone } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ReadMoreButton from '../components/ReadMoreButton';
import Modal from '../components/Modal';
import { motion } from 'framer-motion';
import VideoModal from '../components/VideoModal';
import HeroSliderSkeleton from '../components/HeroSliderSkeleton';
import { Home as HomeIcon, MessageSquareMore } from 'lucide-react';
import { Helmet } from 'react-helmet-async';


interface HeroSlide {
  id: string;
  image_url: string;
  title: string;
  subtitle: string;
  order: number;
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  image_url: string;
}

interface Video {
  id: string;
  video_id: string;
  title: string;
  description: string;
}

interface Project {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  completion_date: string;
  location: string;
  featured: boolean;
  status: 'ongoing' | 'completed' | 'upcoming';
}

const services = [
  {
    title: "Custom Home Building",
    description: "Create your dream home from the ground up with our expert builders.",
    icon: <HomeIcon className="w-8 h-8 text-primary-600" />
  },
  {
    title: "Renovations",
    description: "Transform your existing space into something extraordinary.",
    icon: <Wrench className="w-8 h-8 text-primary-600" />
  },
  {
    title: "Consulting",
    description: "Expert advice for your construction and renovation projects.",
    icon: <MessageSquareMore className="w-8 h-8 text-primary-600" />
  }
] as const;

const Home = () => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [featuredVideos, setFeaturedVideos] = useState<Video[]>([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: heroData, error: heroError } = await supabase
          .from('hero_slides')
          .select('*')
          .order('order');

        const { data: testimonialData, error: testimonialError } = await supabase
          .from('testimonials')
          .select('*')
          .order('created_at', { ascending: false });

        const { data: videoData, error: videoError } = await supabase
          .from('youtube_videos')
          .select('*')
          .limit(3);

        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('featured', true)
          .order('created_at', { ascending: false });

        if (heroError) console.error('Error fetching hero slides:', heroError);
        if (testimonialError) console.error('Error fetching testimonials:', testimonialError);
        if (videoError) console.error('Error fetching videos:', videoError);
        if (projectError) throw projectError;

        if (heroData) setSlides(heroData);
        if (testimonialData) setTestimonials(testimonialData);
        if (videoData) setFeaturedVideos(videoData);
        if (projectData) setFeaturedProjects(projectData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || testimonials.length <= 3) return;

    scrollContainer.scrollLeft = 0;
    let animationFrameId: number;
    const speed = 1;
    let isScrolling = true;

    const scroll = () => {
      if (!isScrolling || !scrollContainer) return;

      scrollContainer.scrollLeft += speed;

      if (scrollContainer.scrollLeft >= (scrollContainer.scrollWidth - scrollContainer.clientWidth) / 2) {
        scrollContainer.style.scrollBehavior = 'auto';
        scrollContainer.scrollLeft = 0;
        scrollContainer.style.scrollBehavior = 'smooth';
      }

      animationFrameId = requestAnimationFrame(scroll);
    };

    const timeoutId = setTimeout(() => {
      animationFrameId = requestAnimationFrame(scroll);
    }, 1000);

    return () => {
      isScrolling = false;
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
      if (scrollContainer) {
        scrollContainer.style.scrollBehavior = 'auto';
        scrollContainer.scrollLeft = 0;
      }
    };
  }, [testimonials.length]);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    servicesSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div>
      <Helmet>
        <title>Premium Custom Home Builders in Kannur, Kerala | Master Home</title>
        <meta name="description" content="Master Home is the leading premium custom home builder and construction company in Kannur, Kerala. Transform your living space with our expert renovations." />
        <link rel="canonical" href="https://masterhome.co.in/" />
      </Helmet>
      <h1 className="sr-only">Premium Custom Home Builders in Kannur, Kerala</h1>
      {/* Hero Section */}
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        pagination={{ clickable: true }}
        autoplay={!isLoading && slides.length > 1 ? { delay: 5000, disableOnInteraction: false } : false}
        loop={!isLoading && slides.length > 1}
        className="h-screen relative"
      >
        {isLoading ? (
          <SwiperSlide>
            <HeroSliderSkeleton />
          </SwiperSlide>
        ) : slides.length > 0 ? (
          slides.map((slide: HeroSlide) => (
            <SwiperSlide key={slide.id}>
              <div
                className="relative h-screen bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.image_url})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30" />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute inset-0 flex items-center"
                >
                  <div className="container-padding">
                    <div className="max-w-3xl">
                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6"
                      >
                        {slide.title}
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-white/90 mb-8"
                      >
                        {slide.subtitle}
                      </motion.p>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex flex-wrap gap-4"
                      >
                        <button
                          onClick={scrollToServices}
                          className="btn-primary flex items-center group"
                        >
                          Learn More
                          <ChevronDown className="w-5 h-5 ml-2 animate-bounce" />
                        </button>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide>
            <div
              className="relative h-screen bg-cover bg-center bg-no-repeat bg-gray-800"
              style={{ backgroundImage: `url(./about-image.jpg)` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute inset-0 flex items-center"
              >
                <div className="container-padding">
                  <div className="max-w-3xl">
                    <motion.h2
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6"
                    >
                      Welcome to Master Home
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="text-xl md:text-2xl text-white/90 mb-8"
                    >
                      Building excellence with passion and precision.
                    </motion.p>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="flex flex-wrap gap-4"
                    >
                      <button
                        onClick={scrollToServices}
                        className="btn-primary flex items-center group"
                      >
                        Our Services
                        <ChevronDown className="w-5 h-5 ml-2 animate-bounce" />
                      </button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </SwiperSlide>
        )}
      </Swiper>

      {/* About Preview */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-primary-50 rounded-full">

              </div>
              <h2 className="text-4xl font-bold leading-tight">
                Building Dreams Into Reality
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Master Home has been crafting exceptional living spaces
                that reflect our commitment to quality, innovation, and customer satisfaction.
                Our journey from a small family business to a leading name in custom home
                building is built on trust, expertise, and unwavering dedication to excellence.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/about"
                  className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-md hover:shadow-lg group"
                >
                  Learn more about our story
                  <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="tel:+919567810944" className="inline-flex items-center px-6 py-3 text-lg font-medium text-primary-700 bg-primary-50 rounded-xl hover:bg-primary-100 transition-colors border border-primary-200">
                  <Phone className="w-5 h-5 mr-2" /> Talk to Builder
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl transform rotate-2 opacity-10"></div>
              <img
                src="https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?auto=format&fit=crop&w=800&q=80"
                alt="Modern home interior"
                className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Add this section between About and Services */}
      <section className="py-24 bg-gradient-to-br from-primary-600 via-primary-500 to-primary-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-primary-500/10 to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent"></div>
          <div className="absolute inset-0 pattern-dots opacity-10"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {[
              { number: '50+', label: 'Projects Completed' },
              { number: '10+', label: 'Years Experience' },
              { number: '100%', label: 'Client Satisfaction' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="glass-card hover:bg-white/20 transition-all p-6 md:p-8 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 + 0.3 }}
                  className="text-3xl md:text-4xl font-bold text-white mb-2"
                >
                  {stat.number}
                </motion.div>
                <div className="text-primary-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Trust Badges */}
      <div className="bg-white border-b border-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold text-gray-400 mb-8 uppercase tracking-wider">Trusted Construction Materials Partner</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-20 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            <span className="text-2xl md:text-3xl font-black text-gray-800 tracking-tighter">UltraTech<span className="text-yellow-500">Cement</span></span>
            <span className="text-2xl md:text-3xl font-black text-blue-900 tracking-tight">JSW <span className="text-red-600">STEEL</span></span>
            <span className="text-2xl md:text-3xl font-black text-red-700 tracking-wide">Kajaria</span>
            <span className="text-lg md:text-xl font-bold flex items-center gap-2 text-gray-700 whitespace-nowrap"><CheckCircle2 className="text-green-500 w-5 h-5 md:w-6 md:h-6" /> Kerala Builders Assoc.</span>
          </div>
        </div>
      </div>

      {/* Services Preview */}
      <section id="services" className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-50/30 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gradient">Our Services</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-4">
              Comprehensive solutions for your home building and renovation needs
            </p>
            <p className="text-gray-500 text-sm max-w-3xl mx-auto">
              Trusted <Link to="/locations/kannur" className="text-primary-600 hover:underline">Home builders in Kannur</Link> delivering excellence. Expanding our reach with premier <Link to="/locations/iritty" className="text-primary-600 hover:underline">Construction services in Iritty</Link> and custom <Link to="/locations/taliparamba" className="text-primary-600 hover:underline">villa builders in Taliparamba</Link>.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="glass-card backdrop-blur-sm p-6 md:p-8 group cursor-pointer"
              >
                <div className="mb-6 bg-primary-50 w-12 h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-4 group-hover:text-primary-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      {featuredProjects.length > 0 && (
        <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
          <div className="container-padding">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Our Featured Projects
              </h2>
              <p className="text-gray-600">
                Explore some of our most remarkable projects that showcase our commitment to quality and innovation in construction.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {featuredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-2xl bg-white shadow-xl"
                >
                  <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                    <p className="text-white/90 mb-4 line-clamp-2">
                      {project.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-white/80">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        <span>{project.category}</span>
                      </div>
                      {project.completion_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(project.completion_date).getFullYear()}</span>
                        </div>
                      )}
                      {project.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{project.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
              <Link
                to="/projects"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                View All Projects
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-3 text-primary-700 bg-white border-2 border-primary-100 rounded-full font-bold hover:bg-gray-50 transition-colors shadow-sm">
                Get a Free Quote
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Featured Learning Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gradient">Featured Learning Content</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover expert tips and insights about home building
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-12">
            {featuredVideos.map((video: Video) => (
              <div
                key={video.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 relative cursor-pointer"
                onClick={() => setSelectedVideo(video)}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-primary-600/90 via-primary-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"></div>
                <div className="relative pt-[56.25%] bg-gray-900">
                  <img
                    src={`https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`}
                    alt={video.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-8 relative z-20">
                  <div className="transform group-hover:translate-y-[-8px] transition-transform duration-300">
                    <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 group-hover:text-white transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-gray-600 group-hover:text-white/90 text-sm md:text-base leading-relaxed line-clamp-3 transition-colors">
                      {video.description}
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 z-30">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                    <svg
                      className="w-8 h-8 text-primary-600 transform translate-x-0.5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/learning-center"
              className="inline-flex items-center text-lg font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              View all learning content
              <ArrowRight className="ml-2 h-5 w-5 animate-pulse" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-br from-white via-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-primary-50/30 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 overflow-hidden relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gradient">What Our Clients Say</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Real experiences from our satisfied customers
            </p>
          </motion.div>
          <div className="relative">
            <div
              ref={scrollRef}
              className="flex gap-4 md:gap-8 overflow-x-auto no-scrollbar px-4"
              style={{
                maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
              }}
            >
              {testimonials.map((testimonial: Testimonial, index: number) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-white to-gray-50 p-4 md:p-8 rounded-2xl shadow-lg flex-shrink-0 flex flex-col min-w-[280px] md:min-w-[350px] max-w-[320px] md:max-w-[450px] relative border border-gray-100 group hover:shadow-xl transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-50/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
                  <div className="relative z-10">
                    <div className="flex items-center mb-4 md:mb-6">
                      <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0">
                        <img
                          src={testimonial.image_url}
                          alt={testimonial.name}
                          className="w-full h-full rounded-full object-cover ring-4 ring-primary-50"
                        />
                      </div>
                      <div className="ml-3 md:ml-4 flex-grow">
                        <h4 className="font-bold text-lg md:text-xl">{testimonial.name}</h4>
                        <p className="text-gray-600 text-sm md:text-base">{testimonial.role}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed line-clamp-4 mb-4">
                      {testimonial.content}
                    </p>
                    {testimonial.content.length > 168 && (
                      <ReadMoreButton
                        onClick={() => setSelectedTestimonial(testimonial)}
                        className="mt-auto text-sm md:text-base"
                      />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Add Modal */}
      <Modal
        isOpen={!!selectedTestimonial}
        onClose={() => setSelectedTestimonial(null)}
      >
        {selectedTestimonial && (
          <div>
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 flex-shrink-0">
                <img
                  src={selectedTestimonial.image_url}
                  alt={selectedTestimonial.name}
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <div className="ml-4">
                <h3 className="text-2xl font-bold">{selectedTestimonial.name}</h3>
                <p className="text-gray-600 text-lg">{selectedTestimonial.role}</p>
              </div>
            </div>
            <div className="prose max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
                {selectedTestimonial.content}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Video Modal */}
      <VideoModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.video_id || ''}
        title={selectedVideo?.title || ''}
      />
    </div>
  );
};

export default Home;
